import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Printer, ArrowUp, ChevronRight } from "lucide-react";
import { SITE } from "@/lib/site";

const EFFECTIVE_DATE = "June 2, 2025";
const LAST_UPDATED = "September 27, 2026";

const SECTIONS = [
  {
    n: 1, title: "Information We Collect",
    body: [
      { p: "Depending on how you interact with us, we may collect information such as:" },
      { ul: [
        "Name", "Address", "Telephone number", "Email address", "Emergency contact information",
        "Information provided when requesting services", "Information necessary to coordinate care",
        "Website and communication preferences", "Information submitted through online forms",
        "Other information voluntarily provided to us",
      ]},
      { p: "When information relates to an individual's health or care, additional privacy protections may apply under applicable federal and state laws, including the Health Insurance Portability and Accountability Act (HIPAA), where applicable." },
    ],
  },
  {
    n: 2, title: "How We Use Information",
    body: [
      { p: "Melo Hopeful Care may use information to:" },
      { ul: [
        "Respond to inquiries and requests",
        "Communicate with clients, families, caregivers, and authorized representatives",
        "Coordinate and provide services", "Schedule appointments or service visits",
        "Maintain appropriate business and service records", "Process payments and billing",
        "Improve our services and website", "Communicate important information about our services",
        "Meet legal, regulatory, and contractual obligations",
        "Protect the safety, security, and rights of our clients, employees, and organization",
      ]},
      { p: "We will use information only as permitted by applicable law and our organizational policies." },
    ],
  },
  {
    n: 3, title: "Health Information",
    body: [
      { p: "Information concerning an individual's health or care may constitute protected health information (\u201CPHI\u201D) under HIPAA." },
      { p: "When HIPAA applies to Melo Hopeful Care LLC, we maintain policies and safeguards designed to protect PHI from unauthorized access, use, or disclosure." },
      { p: "For more information about how we handle PHI and your rights regarding that information, please review our HIPAA Notice of Privacy Practices." },
    ],
  },
  {
    n: 4, title: "Information Sharing and Disclosure",
    body: [
      { p: "We do not sell personal information for monetary consideration." },
      { p: "We may disclose information when permitted or required by law, including when necessary to:" },
      { ul: [
        "Provide or coordinate requested services",
        "Work with authorized caregivers, representatives, or service providers",
        "Process payments or conduct appropriate business operations",
        "Comply with legal or regulatory requirements",
        "Respond to lawful requests from governmental authorities",
        "Protect the health or safety of an individual when permitted by law",
        "Prevent fraud, abuse, or unlawful activity",
        "Protect the rights, property, and safety of Melo Hopeful Care and others",
      ]},
      { p: "Where required, we seek appropriate authorization before using or disclosing information." },
    ],
  },
  {
    n: 5, title: "Website Information",
    body: [
      { p: "Our website may automatically collect limited technical information, such as:" },
      { ul: [
        "IP address", "Browser type", "Device type", "Operating system", "Pages visited",
        "General website usage information", "Date and time of website access",
      ]},
      { p: "This information may be used to maintain website security, understand website usage, troubleshoot technical issues, and improve the user experience." },
    ],
  },
  {
    n: 6, title: "Cookies and Similar Technologies",
    body: [
      { p: "Our website may use cookies or similar technologies to improve functionality, analyze website traffic, remember preferences, or support website security." },
      { p: "You may be able to adjust your browser settings to control cookies. Disabling certain cookies may affect website functionality." },
    ],
  },
  {
    n: 7, title: "Third-Party Services",
    body: [
      { p: "Our website may contain links to third-party websites, applications, or services." },
      { p: "Melo Hopeful Care LLC is not responsible for the privacy practices, security, or content of third-party websites. We encourage visitors to review the privacy policies of any third-party service they choose to use." },
    ],
  },
  {
    n: 8, title: "Communications",
    body: [
      { p: "If you provide your telephone number or email address, we may use that information to respond to your request, communicate regarding services, or provide important business or care-related information." },
      { p: "We will follow applicable laws and organizational policies when sending communications involving protected or sensitive information." },
    ],
  },
  {
    n: 9, title: "Security",
    body: [
      { p: "Melo Hopeful Care LLC maintains reasonable administrative, technical, and physical safeguards designed to protect information against unauthorized access, use, alteration, disclosure, or destruction." },
      { p: "No method of electronic transmission or storage can be guaranteed to be completely secure. For this reason, we encourage individuals to avoid submitting highly sensitive information through unsecured website forms or ordinary email unless specifically instructed to do so." },
    ],
  },
  {
    n: 10, title: "Children's Privacy",
    body: [
      { p: "Our website is intended for individuals and families seeking information about Melo Hopeful Care's services. We do not knowingly collect personal information from children through our website for purposes unrelated to providing or coordinating authorized services." },
    ],
  },
  {
    n: 11, title: "Your Privacy Choices",
    body: [
      { p: "Depending on the type of information involved and applicable law, you may have rights regarding your personal information, including the ability to:" },
      { ul: [
        "Request information about how your information is used",
        "Request access to certain information",
        "Request correction of inaccurate information",
        "Request certain restrictions or limitations",
        "Withdraw consent where applicable",
        "Request information about disclosures where required by law",
      ]},
      { p: "Additional rights may apply to protected health information under HIPAA." },
    ],
  },
  {
    n: 12, title: "Changes to This Privacy Policy",
    body: [
      { p: "Melo Hopeful Care LLC may update this Privacy Policy periodically to reflect changes in our practices, technology, services, or applicable legal requirements." },
      { p: "The updated policy will be posted on this page with a revised \u201CLast Updated\u201D date." },
    ],
  },
  {
    n: 13, title: "Contact Us", contact: true,
    body: [
      { p: "If you have questions about this Privacy Policy or our privacy practices, please contact:" },
    ],
  },
];

const slug = (n, title) => `s${n}-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`;

function SectionBody({ blocks }) {
  return (
    <div className="space-y-3">
      {blocks.map((b, i) =>
        b.ul ? (
          <ul key={i} className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 pl-0">
            {b.ul.map((li) => (
              <li key={li} className="flex items-start gap-2 text-slate-700 leading-relaxed">
                <span className="mt-2 w-1.5 h-1.5 rounded-full bg-teal-500 shrink-0" />
                <span>{li}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p key={i} className="text-slate-700 leading-relaxed">{b.p}</p>
        )
      )}
    </div>
  );
}

function ContactCard() {
  return (
    <address className="not-italic mt-4 soft-card !p-6 text-slate-700 leading-relaxed" data-testid="privacy-contact-card">
      <div className="font-heading font-semibold text-slate-900">{SITE.name}</div>
      <div>{SITE.address.line1}</div>
      <div>{SITE.address.line2}</div>
      <div className="mt-3">
        Phone:{" "}
        {SITE.phones.map((p, i) => (
          <span key={p.tel}>
            {i > 0 && " / "}
            <a href={`tel:${p.tel}`} className="text-brand-navy hover:underline">{p.display}</a>
          </span>
        ))}
      </div>
      <div>Email: <a href={`mailto:${SITE.email}`} className="text-brand-navy hover:underline">{SITE.email}</a></div>
      <p className="mt-4 text-sm text-slate-600">
        For questions specifically concerning protected health information, please review our HIPAA Notice of Privacy Practices or contact us using the information above.
      </p>
    </address>
  );
}

export default function Privacy() {
  const [active, setActive] = useState(SECTIONS[0].n);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            const n = Number(e.target.getAttribute("data-section-n"));
            if (n) setActive(n);
          }
        });
      },
      { rootMargin: "-40% 0px -55% 0px", threshold: 0 }
    );
    document.querySelectorAll("[data-section-n]").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div data-testid="privacy-page">
      <section className="relative overflow-hidden bg-[#072A47] text-white">
        <div className="grain opacity-20" />
        <div className="container-x pt-16 pb-14 md:pt-20 md:pb-16">
          <div className="flex items-center gap-3 text-teal-200/90">
            <ShieldCheck className="w-4 h-4" />
            <span className="text-xs uppercase tracking-[0.22em] font-semibold">Legal</span>
          </div>
          <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-heading font-semibold tracking-tight leading-[1.05]">
            Privacy Policy
          </h1>
          <p className="mt-4 text-white/80 max-w-2xl leading-relaxed">
            How Melo Hopeful Care LLC collects, uses, protects, and discloses information when you visit our website, contact us, or interact with our services.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3 text-sm">
            <span className="pill bg-white/10 border border-white/20 text-white/90" data-testid="privacy-effective-date">Effective {EFFECTIVE_DATE}</span>
            <span className="pill bg-white/10 border border-white/20 text-white/90" data-testid="privacy-updated-date">Last updated {LAST_UPDATED}</span>
            <button onClick={() => window.print()} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 text-sm font-medium transition print:hidden" data-testid="privacy-print-btn">
              <Printer className="w-4 h-4" /> Print / Save as PDF
            </button>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container-x grid lg:grid-cols-12 gap-10">
          <aside className="lg:col-span-4 xl:col-span-3 print:hidden" data-testid="privacy-toc">
            <div className="lg:sticky lg:top-28 max-h-[calc(100vh-8rem)] overflow-y-auto pr-2">
              <div className="text-xs uppercase tracking-[0.22em] text-teal-700 font-semibold mb-4">On this page</div>
              <nav className="space-y-0.5 border-l border-slate-200">
                {SECTIONS.map((s) => (
                  <a
                    key={s.n}
                    href={`#${slug(s.n, s.title)}`}
                    data-testid={`privacy-toc-link-${s.n}`}
                    className={`group flex items-start gap-2 pl-4 pr-2 py-1.5 -ml-px border-l-2 text-[13px] leading-snug transition ${
                      active === s.n
                        ? "border-teal-500 text-brand-navy font-semibold bg-teal-50/40"
                        : "border-transparent text-slate-600 hover:text-brand-navy hover:border-slate-300"
                    }`}
                  >
                    <span className="text-slate-400 tabular-nums shrink-0 w-6 text-right">{s.n}.</span>
                    <span className="flex-1">{s.title}</span>
                    <ChevronRight className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${active === s.n ? "text-teal-600" : "text-transparent group-hover:text-slate-400"}`} />
                  </a>
                ))}
              </nav>
            </div>
          </aside>

          <article className="lg:col-span-8 xl:col-span-9 max-w-3xl" data-testid="privacy-body">
            <div className="soft-card !p-8 md:!p-10">
              <p className="text-slate-700 leading-relaxed">
                Melo Hopeful Care LLC (“Melo Hopeful Care,” “we,” “us,” or “our”) respects the privacy of the individuals and families we serve. We are committed to protecting personal information entrusted to us and maintaining appropriate safeguards for information collected through our website, communications, and services.
              </p>
              <p className="mt-4 text-slate-700 leading-relaxed">
                This Privacy Policy explains how we collect, use, protect, and disclose information when you visit our website, contact us, request information, or interact with Melo Hopeful Care LLC.
              </p>
              <div className="mt-6 pt-6 border-t border-slate-200 text-sm text-slate-600 leading-relaxed" data-testid="privacy-company-block">
                <div className="font-semibold text-slate-900">{SITE.name}</div>
                <div>{SITE.address.oneLine}</div>
                <div>Phone: {SITE.phones.map((p) => p.display).join(" / ")}</div>
                <div>Email: <a href={`mailto:${SITE.email}`} className="text-brand-navy hover:underline">{SITE.email}</a></div>
              </div>
            </div>

            <div className="mt-10 space-y-10">
              {SECTIONS.map((s) => (
                <section key={s.n} id={slug(s.n, s.title)} data-section-n={s.n} data-testid={`privacy-section-${s.n}`} className="scroll-mt-28">
                  <span className="text-xs uppercase tracking-[0.22em] font-semibold text-teal-700 tabular-nums">
                    Section {String(s.n).padStart(2, "0")}
                  </span>
                  <h2 className="mt-2 text-2xl sm:text-3xl font-heading font-semibold text-slate-900 tracking-tight">{s.title}</h2>
                  <div className="mt-4">
                    <SectionBody blocks={s.body} />
                    {s.contact && <ContactCard />}
                  </div>
                </section>
              ))}
            </div>

            <blockquote className="mt-12 border-l-4 border-teal-500 pl-5 text-slate-700 italic" data-testid="privacy-closing">
              Your privacy matters to us. Melo Hopeful Care LLC is committed to treating the information entrusted to us with care, respect, and appropriate confidentiality.
            </blockquote>

            <div className="mt-12 pt-8 border-t border-slate-200 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
              <div className="text-sm text-slate-500">Last updated: {LAST_UPDATED}</div>
              <div className="flex items-center gap-3 print:hidden">
                <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="btn-ghost" data-testid="privacy-back-to-top">
                  <ArrowUp className="w-4 h-4" /> Back to top
                </button>
                <Link to="/terms" className="btn-outline" data-testid="privacy-terms-link">Terms &amp; Conditions</Link>
              </div>
            </div>
          </article>
        </div>
      </section>
    </div>
  );
}
